/**
 * EU energy efficiency class ladder (A+++ … G) with the product's class marked.
 *
 * Not a chart in the usual sense: the data's job is IDENTITY on an ordinal scale —
 * one value out of a fixed, legally defined set — so the form is a labelled ladder
 * with the value called out directly, not a bar chart with an axis. No legend: every
 * band carries its own label.
 *
 * The colours are NOT a design choice. The green→red sequence is prescribed for the
 * EU energy label, so the palette is fixed and the only accessibility freedom left is
 * the text on top: each band declares its own ink so the label stays legible on the
 * dark green/red bands as well as on the yellows. Identity never rests on colour
 * alone — the class letter is written in the band and repeated in the callout.
 *
 * Data comes from the product document (EPREL encodes A+++ as "APPP", A++ as "APP",
 * A+ as "AP"), so this renders without an extra request.
 */

import "../App.sass";
import { useTranslation } from "react-i18next";

interface Properties {
  /** class as stored on the product, e.g. "APPP" | "A" | "C" */
  energyClass?: string | null;
  /** optional second scale, e.g. water heating class of a heat pump */
  secondaryClass?: string | null;
  secondaryLabel?: string;
}

/** EU label bands, best → worst, with the ink that stays legible on each. */
const BANDS: { key: string; label: string; fill: string; ink: string }[] = [
  { key: "APPP", label: "A+++", fill: "#00a650", ink: "#ffffff" },
  { key: "APP", label: "A++", fill: "#3aaa35", ink: "#ffffff" },
  { key: "AP", label: "A+", fill: "#4cb847", ink: "#ffffff" },
  { key: "A", label: "A", fill: "#bfd62e", ink: "#1d2b12" },
  { key: "B", label: "B", fill: "#fff200", ink: "#1d2b12" },
  { key: "C", label: "C", fill: "#fdb913", ink: "#1d2b12" },
  { key: "D", label: "D", fill: "#f37021", ink: "#ffffff" },
  { key: "E", label: "E", fill: "#ee3124", ink: "#ffffff" },
  { key: "F", label: "F", fill: "#d81920", ink: "#ffffff" },
  { key: "G", label: "G", fill: "#b31217", ink: "#ffffff" },
];

/** "APPP" → "A+++"; unknown values are shown verbatim rather than swallowed. */
export const decodeEnergyClass = (raw?: string | null): string | null => {
  if (!raw) return null;
  const key = String(raw).trim().toUpperCase();
  const band = BANDS.find(b => b.key === key);
  return band ? band.label : key;
};

export default function EnergyClassLadder(properties: Properties) {
  const { t } = useTranslation();
  const current = String(properties.energyClass || "").trim().toUpperCase();
  const activeIndex = BANDS.findIndex(b => b.key === current);
  const secondary = decodeEnergyClass(properties.secondaryClass);

  if (!current) return null;

  return (
    <div>
      <div style={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 420 }}>
        {BANDS.map((band, index) => {
          const active = index === activeIndex;
          // the classic label geometry: bands grow towards the worse end
          const width = 45 + index * 6;
          return (
            <div key={band.key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div
                style={{
                  width: `${width}%`,
                  background: band.fill,
                  color: band.ink,
                  padding: "2px 10px",
                  borderRadius: "3px 12px 12px 3px",
                  fontWeight: active ? 700 : 400,
                  fontSize: active ? 14 : 12,
                  opacity: activeIndex >= 0 && !active ? 0.45 : 1,
                  outline: active ? "2px solid #1d2b12" : "none",
                }}
              >
                {band.label}
              </div>
              {active ? (
                <span style={{ fontWeight: 600 }}>
                  {t("settingsPanel.productEligibilityPanel.thisProduct")}
                </span>
              ) : null}
            </div>
          );
        })}
      </div>

      {activeIndex < 0 ? (
        <div className="marginTopS" style={{ fontSize: 12, color: "#6b7480" }}>
          {t("settingsPanel.productEligibilityPanel.unknownClass", { value: current })}
        </div>
      ) : null}

      {secondary ? (
        <div className="marginTopS" style={{ fontSize: 12, color: "#4a5560" }}>
          {properties.secondaryLabel ||
            t("settingsPanel.productEligibilityPanel.waterHeatingClass")}
          : <strong>{secondary}</strong>
        </div>
      ) : null}
    </div>
  );
}
